import {
  BILLING_GUIDE_CAPTURE_NOTE,
  BILLING_GUIDE_LABELS,
  BILLING_GUIDE_RECIPES,
  BILLING_GUIDE_SECTIONS,
  BILLING_GUIDE_SETTINGS,
} from '@admin-platform/shared/integration-content';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Figure, Timeline } from '../components/Diagram';

interface Row {
  key: string;
  name: string;
  blurb: string;
}

const TIMINGS: Row[] = [
  {
    key: 'ADVANCE',
    name: 'Advance',
    blurb:
      'Billed at the start of the period, before usage happens. Standard for flat, recurring charges — the customer pays upfront for the cycle ahead.',
  },
  {
    key: 'ARREARS',
    name: 'Arrears',
    blurb:
      'Billed at the end of the period, once usage for that period is fully known. Invoice generation excludes arrears components until the cycle actually closes, then bills what was consumed.',
  },
];

const LEVELS: Row[] = [
  {
    key: 'DEFAULT',
    name: 'Component default',
    blurb:
      'Every Variable Component has a billingTiming field — this is what every subscription inherits unless something more specific overrides it.',
  },
  {
    key: 'SUBSCRIPTION',
    name: 'Per-subscription override',
    blurb:
      'One customer’s copy of a component can be pinned to Advance or Arrears independent of the product default, with a required reason for the audit trail. Set to null to fall back to whatever the live product default is.',
  },
  {
    key: 'AGREEMENT',
    name: 'Custom Pricing Agreement override',
    blurb:
      'A Custom Pricing Agreement can opt BILLING_TIMING into its overrideScope, pinning timing as part of a broader negotiated deal rather than a standalone toggle.',
  },
];

export function BillingTimingGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Advance vs. arrears billing — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Advance vs. arrears billing</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Pay <em>before</em> or pay <em>after</em>.
        </h1>
        <p className="dp-oneliner">
          Every subscription bills its base price ahead of each period. Each Variable Component is
          billed either ahead with the base (Advance) or after the period ends (Arrears), and a
          charge counted per period needs a final figure before its invoice goes out. Timing can be
          pinned per product, per subscription, or per negotiated deal — and a change never touches
          a cycle already in flight.
        </p>
      </header>

      <section className="dp-section">
        <h2>What each timing means</h2>
        <div className="dp-fields-table">
          {TIMINGS.map((t) => (
            <div className="dp-field-row" key={t.key}>
              <div className="dp-field-k">{t.name}</div>
              <div className="dp-field-v">{t.blurb}</div>
            </div>
          ))}
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Advance is the right default for flat, predictable charges — the customer knows the amount
          before the period starts, so there is nothing to wait for. Arrears is the right choice for
          anything metered, where the final amount can only be known once the period has actually
          happened.
        </p>
        <Figure
          title="Advance and arrears in one period"
          caption="Advance bills before the period happens; arrears waits until the cycle closes."
        >
          <Timeline
            items={[
              {
                when: 'Period start',
                title: 'Advance invoiced',
                text: 'The customer pays for the cycle ahead.',
                tone: 'accent',
              },
              {
                when: 'During the period',
                title: 'Usage happens',
                text: 'Arrears components are held out of invoicing.',
                tone: 'muted',
              },
              {
                when: 'Period end',
                title: 'Cycle closes',
                text: 'Usage for the period is fully known.',
              },
              {
                when: 'After close',
                title: 'Arrears invoiced',
                text: 'Bills what was consumed.',
                tone: 'warn',
              },
            ]}
          />
        </Figure>
      </section>

      {BILLING_GUIDE_SECTIONS.map((section) => (
        <section className="dp-section" key={section.id}>
          <h2>{section.title}</h2>
          <ul className="dp-plain">
            {section.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>
      ))}

      <section className="dp-section">
        <h2>The settings that decide it</h2>
        <div className="dp-fields-table">
          {BILLING_GUIDE_SETTINGS.map((setting) => (
            <div className="dp-field-row" key={setting.field}>
              <div className="dp-field-k">
                {setting.title}
                <br />
                <code>{setting.field}</code>
              </div>
              <div className="dp-field-v">{setting.text}</div>
            </div>
          ))}
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          {BILLING_GUIDE_CAPTURE_NOTE}
        </p>
      </section>

      <section className="dp-section">
        <h2>Choosing settings for a charge</h2>
        <div className="dp-fields-table">
          {BILLING_GUIDE_RECIPES.map((recipe) => (
            <div className="dp-field-row" key={recipe.charge}>
              <div className="dp-field-k">{recipe.charge}</div>
              <div className="dp-field-v">
                <strong>
                  {BILLING_GUIDE_LABELS[recipe.timing]} · {BILLING_GUIDE_LABELS[recipe.resets]}
                  {recipe.ifSilent ? ` · If silent: ${BILLING_GUIDE_LABELS[recipe.ifSilent]}` : ''}
                  {` · Usage capture: ${recipe.usageCapture}`}
                </strong>
                <br />
                {recipe.invoiced}. Needs someone: {recipe.needsSomeone.toLowerCase()}.
              </div>
            </div>
          ))}
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          In the dashboard, the same guide opens from <strong>How billing works</strong> on the
          Catalog page, and each subscription shows its own next invoices under{' '}
          <strong>How it bills</strong>.
        </p>
      </section>

      <section className="dp-section">
        <h2>Three levels, most specific wins</h2>
        <ol className="qs-steps">
          {LEVELS.map((l, i) => (
            <li className="qs-step" key={l.key}>
              <span className="qs-phase-num" style={{ marginRight: 12 }}>
                {i + 1}
              </span>
              <div>
                <div className="qs-step-label">{l.name}</div>
                <div className="qs-step-detail">{l.blurb}</div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="dp-section">
        <h2>Changes are prospective, by design</h2>
        <p className="dp-plain">
          A billing timing override never rewrites the invoice for a cycle that has already been
          scheduled or generated. It takes effect at the next period reset boundary — the same point
          where component pricing and usage counters reset. Flipping a component from Advance to
          Arrears mid-cycle doesn’t retroactively delay the invoice you already sent; it changes how
          the <em>next</em> cycle is billed.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Because of this, the dashboard always shows three things side by side when you’re looking
          at a component’s timing: the product default, what’s actually billing this cycle, and what
          will bill next cycle if an override is pending. A reason is required for every override,
          and the full history — who changed it, when, and why — is kept separately for audit.
        </p>
      </section>

      <section className="dp-section">
        <h2>Next up</h2>
        <ul className="dp-connects">
          <li>
            <Link
              to="/docs/guides/pricing-models"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Pricing models reference ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/custom-agreements"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Custom Pricing Agreements ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/override-pricing"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Overriding base &amp; component pricing ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
